import { Dispatch, SetStateAction } from "react";
import { IonInput } from "@ionic/react";
import { IoHelpCircle } from "react-icons/io5";
import { Asterisk, Tooltip } from "swing-components";

import { ErrorAlert } from "~components/ErrorAlert/ErrorAlert";
import { translate } from "~utils";

type InternalCodeFieldProps = {
  internalCode: string;
  setInternalCode: Dispatch<SetStateAction<string>>;
  required: boolean;
  formSubmissionErrObj?: Record<string | "internalCode", string>;
  onFormSubmissionError?: (
    error: (error: Record<string, string>) => Record<string, string> & { internalCode?: string },
  ) => void;
  onRequiredFieldTouched: (e: (touched: object) => { internalCode: boolean }) => void;
};

export function InternalCodeField(props: InternalCodeFieldProps) {
  const {
    internalCode,
    setInternalCode,
    required,
    onFormSubmissionError,
    onRequiredFieldTouched,
    formSubmissionErrObj,
  } = props;

  const handleChange = (newInternalCode: string) => {
    setInternalCode(newInternalCode);
    if (required) {
      onRequiredFieldTouched((touched: object) => ({
        ...touched,
        internalCode: true,
      }));
      updateInternalCodeErrorState(newInternalCode, true, onFormSubmissionError);
    }
  };
  return (
    <div
      css={{
        display: "flex",
        flexDirection: "column",
        // This is to remove the default height of the `ion-input` from
        // being 42px.
        ".sc-ion-input-md-h": {
          minHeight: "auto !important",
        },
      }}
    >
      <Tooltip
        trigger="present-alert"
        header="Internal Code"
        content="Some organizations use internal codes to assist with invoice and budget reconciliation. Enter yours here if provided."
      />
      <div className="flex items-center">
        <p css={{ margin: 0, fontWeight: 500 }}>{translate("LABEL_INTERNAL_CODE")}</p>
        {required && <Asterisk />}

        <button
          id="present-alert"
          css={{
            border: "none",
            background: "none",
          }}
        >
          <IoHelpCircle
            className="icon-lg"
            css={{ color: "var(--ion-color-primary)", cursor: "pointer" }}
          />
        </button>
      </div>

      {formSubmissionErrObj?.internalCode && (
        <ErrorAlert customStyles={{ alignItems: "center", fontWeight: 400 }}>
          <span>{translate("INTERNAL_CODE_ERROR")}</span>
        </ErrorAlert>
      )}

      <IonInput
        aria-label="Internal Code"
        value={internalCode}
        placeholder="Enter code"
        css={{ marginTop: 18 }}
        onIonInput={(e: CustomEvent) => handleChange(e.detail.value)}
        onIonBlur={() => {
          if (required) {
            updateInternalCodeErrorState(internalCode, true, onFormSubmissionError);
          }
        }}
      />
    </div>
  );
}

/***** Helper Functions *****/
function updateInternalCodeErrorState(
  internalCode: string,
  isInternalCodeTouched: boolean,
  onFormSubmissionError: InternalCodeFieldProps["onFormSubmissionError"],
) {
  onFormSubmissionError?.((error) => ({
    ...error,
    internalCode:
      internalCode.trim() === "" && isInternalCodeTouched ? translate("INTERNAL_CODE_ERROR") : "",
  }));
}
